import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { MdDragHandle } from "react-icons/md";
import Select from "../../Shared/Select";
import Button from "shared/Button";
import styles from "./SubmittedAgendaItem.module.css";
import Markdown from "../../Shared/Markdown";
import MoveModal from "ManagementTeam/Shared/MoveModal";
import formatDate from "shared/@utils/formatDate";
import { toTitleCase } from "shared/@utils/formatString";

export function SubmittedAgendaItem({
  meetingDates,
  assignee,
  assignedType,
  title,
  description,
  isNew,
  onEdit,
  onDelete,
  onAdd,
  readOnly,
}) {
  const actionEdit = "Edit";
  const actionDelete = "Delete";
  const actionMove = "Move";

  const [selectedMeetingDate, setSelectedMeetingDate] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [isMoved, setIsMoved] = useState(false);
  const [options, setOptions] = useState([
    actionEdit,
    actionDelete,
    actionMove,
  ]);

  //Handle the color of the left box
  let leftBoxColor;
  // eslint-disable-next-line default-case
  switch (assignedType) {
    case "Diamond":
      leftBoxColor = "#7F6A45";
      break;
    case "WWG":
      leftBoxColor = "#91B6BB";
      break;
    case "Attorney":
      leftBoxColor = "#ADB7C0";
      break;
  }

  const handleOptionsSelection = (selection) => {
    switch (selection) {
      case actionEdit:
        onEdit(title);
        break;
      case actionDelete:
        onDelete(title);
        break;
      case actionMove:
        setShowMoveModal(true);
        break;

      default:
        break;
    }
  };

  const handleAddItem = (dateSelect) => {
    setIsMoved(true);
    setSelectedMeetingDate(new Date(dateSelect.date));
    onAdd(dateSelect);

    const newOptions = options.filter((a) => a !== actionMove);
    setOptions(newOptions);
  };

  return (
    <>
      <div className={styles.agendaItemDropdownContainer}>
        <MdDragHandle style={{ fontSize: "30px" }} className={styles.dragger} />

        <div className={styles.agendaItemDropdownBody}>
          <div
            className={styles.leftBox}
            style={{ backgroundColor: leftBoxColor }}
          >
            {isNew && (
              <span className={styles.newBubbleContainer}>
                <span className={styles.newBubble} />
              </span>
            )}
          </div>
          <div className={styles.descriptionBox}>
            <span>{toTitleCase(assignee)}</span>
            <span className={styles.bold}>{title}</span>
            {description && (
              <Markdown content={description ? description : ""} />
            )}
          </div>
        </div>

        {!readOnly && (
          <span>
            <Select
              className={styles.selectContainer}
              placeholder="Options"
              options={options}
              onChange={handleOptionsSelection}
              name="Options"
            />
          </span>
        )}

        {isMoved ? (
          <span className={styles.dateSelect}>
            Moved to {formatDate(selectedMeetingDate, "MMM DD YYYY")}
          </span>
        ) : (
          showMoveModal && (
            <MoveModal
              isOpen={showMoveModal}
              onClose={() => {
                setShowMoveModal(false);
              }}
              onMove={(dateSelect) => {
                handleAddItem(dateSelect);
              }}
            />
          )
        )}
      </div>
    </>
  );
}

SubmittedAgendaItem.propTypes = {
  /**Assignee of the decision (Bold Text)  */
  assignee: PropTypes.string,
  /**The type of assigned item, either "Diamond”, "WWG”, or "Attorney”.
   * Left Brown Box (#7F6A45) is Diamond, Left Teal Box (#91B6BB) is WWG, and Left Gray Box (#ADB7C0) is Attorney.  */
  assignedType: PropTypes.string,
  /**Title of the decision item (Regular Text)  */
  title: PropTypes.string,
  /**Description of the details of the decision item (Italic Text)  */
  description: PropTypes.string,
  /**Boolean that holds if the item is new to the list (Shows the green notification mark on the top left corner like the Agenda Item example)  */
  isNew: PropTypes.bool,
  /**Function called when edit is clicked  */
  onEdit: PropTypes.func,
  /**Function called when delete is clicked  */
  onDelete: PropTypes.func,
  /**Function called when the add button is clicked */
  onAdd: PropTypes.func,
};
